import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Language, View } from '../../types';

interface Props {
  lang: Language;
  setView: (v: View) => void;
}

interface Pillar {
  title: {
    en: string;
    fa: string;
  };
  description: {
    en: string;
    fa: string;
  };
}

const pillars: Pillar[] = [
  {
    title: {
      en: "Systems Thinking",
      fa: "تفکر سیستمی"
    },
    description: {
      en: "Your inner system works perfectly for its current goals. We help retune it for the goals you consciously choose.",
      fa: "سیستم درونی شما برای اهداف فعلی‌اش کامل کار می‌کنه. ما کمک می‌کنیم برای اهدافی که آگاهانه انتخاب می‌کنید تنظیمش کنید."
    }
  },
  {
    title: {
      en: "IFS (Internal Family Systems)",
      fa: "سیستم خانواده درونی"
    },
    description: {
      en: "Compassion and negotiation with your inner parts, not fighting them.",
      fa: "شفقت و گفتگو با بخش‌های درونی‌تان، نه جنگیدن با آن‌ها."
    }
  },
  {
    title: {
      en: "NVC (Nonviolent Communication)",
      fa: "ارتباط بدون خشونت"
    },
    description: {
      en: "Deep connection with yourself and others through understanding needs.",
      fa: "ارتباط عمیق با خود و دیگران از طریق درک نیازها."
    }
  }
];

const PhilosophySection: React.FC<Props> = ({ lang, setView }) => {
  const isRTL = lang === 'fa';
  const title = lang === 'en' ? 'My Philosophy' : 'فلسفه من';
  const intro = lang === 'en'
    ? 'A holistic approach to personal transformation, integrating proven methodologies for lasting change.'
    : 'یک رویکرد جامع به تحول شخصی، با ادغام روش‌های اثبات‌شده برای تغییر پایدار.';
  const linkText = lang === 'en' ? 'Explore methodology' : 'کاوش روش‌شناسی';

  return (
    <section className="py-16 md:py-24 px-6 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <div className={`grid md:grid-cols-2 gap-12 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
          {/* Left - Sticky */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="self-start"
          >
            <h2 className="swiss-headline text-3xl md:text-4xl text-brandDark">
              {title}
            </h2>
            <p className="text-gray-500 mt-4 leading-relaxed">
              {intro}
            </p>
            <button
              onClick={() => setView('approach')}
              className="mt-6 inline-flex items-center gap-2 text-primary font-medium text-sm hover:text-teal-700 transition-colors duration-200 group min-h-[44px] py-2"
            >
              {linkText}
              <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180 group-hover:-translate-x-1' : 'group-hover:translate-x-1'} transition-transform duration-200`} />
            </button>
          </motion.div>

          {/* Right - Scrollable items */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? -20 : 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className={`${isRTL ? 'md:border-r md:pr-12' : 'md:border-l md:pl-12'} md:border-gray-200 space-y-12`}
          >
            {pillars.map((pillar, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="flex gap-6"
              >
                <span className="text-4xl md:text-5xl font-bold text-gray-200 leading-none shrink-0">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div>
                  <h3 className="font-bold text-brandDark text-lg mb-2">
                    {pillar.title[lang]}
                  </h3>
                  <p className="text-gray-500 leading-relaxed">
                    {pillar.description[lang]}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PhilosophySection;
